/**
 * <image-to-variant>
 *
 * Product media / thumbnail click or mobile swipe
 * -> find variant connected to this media
 * -> select matching color option.
 *
 * Works with:
 * 1. Dawn dropdown variant picker:
 *    select[name="options[Färg]"]
 *
 * 2. Dawn swatch / button variant picker:
 *    input[type="radio"][name="Färg"]
 *    input[type="radio"][name="options[Färg]"]
 *
 * Requires:
 * Admin -> Product -> Variants -> each color variant has the correct variant image.
 */
class ImageToVariant extends HTMLElement {
  constructor() {
    super();

    this.sectionId = this.dataset.section;
    this.targetOption = this.dataset.targetOption || 'Färg';

    this.section =
      document.getElementById(`shopify-section-${this.sectionId}`) ||
      document.getElementById(`MainProduct-${this.sectionId}`) ||
      document;

    this.onClick = this.onClick.bind(this);
    this.onSlideChanged = this.onSlideChanged.bind(this);
    this.onOptionChange = this.onOptionChange.bind(this);
  }

  connectedCallback() {
    if (!this.section) return;

    this.variants = this.parseVariants();

    if (!this.variants.length) {
      console.warn('[image-to-variant] No variants found.');
      return;
    }

    this.slider = this.section.querySelector('slider-component[id^="GalleryViewer-"]');

    if (this.slider) {
      this.slider.addEventListener('slideChanged', this.onSlideChanged);
    }

    this.section.addEventListener('click', this.onClick);

    const select = this.getTargetSelect();
    if (select) {
      select.addEventListener('change', this.onOptionChange);
    }

    this.getTargetRadios().forEach((radio) => {
      radio.addEventListener('change', this.onOptionChange);
    });
  }

  disconnectedCallback() {
    if (this.slider) {
      this.slider.removeEventListener('slideChanged', this.onSlideChanged);
    }

    if (this.section) {
      this.section.removeEventListener('click', this.onClick);
    }

    const select = this.getTargetSelect();
    if (select) {
      select.removeEventListener('change', this.onOptionChange);
    }

    this.getTargetRadios().forEach((radio) => {
      radio.removeEventListener('change', this.onOptionChange);
    });
  }

  normalize(text) {
    return text
      ? String(text).trim().toLowerCase().replace(/\s+/g, ' ')
      : '';
  }

  parseVariants() {
    const script =
      this.section.querySelector('variant-selects script[type="application/json"]') ||
      this.section.querySelector('variant-radios script[type="application/json"]') ||
      this.section.querySelector('script[type="application/json"]');

    if (!script) return [];

    try {
      const data = JSON.parse(script.textContent);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.warn('[image-to-variant] Could not parse variant JSON.', error);
      return [];
    }
  }

  getMediaIdFromRawValue(rawValue) {
    if (!rawValue) return null;

    const parts = String(rawValue).split('-');
    const lastPart = parts[parts.length - 1];
    const mediaId = parseInt(lastPart, 10);

    return Number.isFinite(mediaId) ? mediaId : null;
  }

  getMediaIdFromClick(event) {
    const targetWithMedia =
      event.target.closest('[data-target]') ||
      event.target.closest('[data-media-id]') ||
      event.target.closest('.product__media-item');

    if (!targetWithMedia) return null;

    const rawMediaId =
      targetWithMedia.dataset.target ||
      targetWithMedia.dataset.mediaId ||
      targetWithMedia.getAttribute('data-media-id');

    return this.getMediaIdFromRawValue(rawMediaId);
  }

  onClick(event) {
    const mediaId = this.getMediaIdFromClick(event);
    if (!mediaId) return;

    this.applyMedia(mediaId);
  }

  onSlideChanged(event) {
    const slide = event.detail && event.detail.currentElement;
    if (!slide || !slide.dataset || !slide.dataset.mediaId) return;

    const mediaId = this.getMediaIdFromRawValue(slide.dataset.mediaId);
    if (!mediaId) return;

    this.applyMedia(mediaId);
  }

  onOptionChange(event) {
    const value = event.target.value;
    if (!value) return;

    this.updateColorBadge(value);
  }

  getTargetSelect() {
    return this.section.querySelector(
      `select[name="options[${CSS.escape(this.targetOption)}]"]`
    );
  }

  getTargetRadios() {
    const targetName = this.normalize(this.targetOption);

    const radios = Array.from(this.section.querySelectorAll('input[type="radio"]'));

    return radios.filter((radio) => {
      const rawName = radio.getAttribute('name') || '';

      const cleanName = rawName
        .replace('options[', '')
        .replace(']', '');

      return this.normalize(cleanName) === targetName;
    });
  }

  getAvailableColorValues() {
    const values = new Map();

    const select = this.getTargetSelect();

    if (select) {
      Array.from(select.options).forEach((option) => {
        values.set(this.normalize(option.value), option.value);
        values.set(this.normalize(option.textContent), option.value);
      });
    }

    this.getTargetRadios().forEach((radio) => {
      values.set(this.normalize(radio.value), radio.value);
    });

    return values;
  }

  findVariantByMediaId(mediaId) {
    return this.variants.find((variant) => {
      return (
        variant.featured_media &&
        parseInt(variant.featured_media.id, 10) === parseInt(mediaId, 10)
      );
    });
  }

  getColorValueFromVariant(variant) {
    const availableValues = this.getAvailableColorValues();

    if (!availableValues.size) {
      console.warn('[image-to-variant] No color option values found.');
      return null;
    }

    const possibleValues = [
      variant.option1,
      variant.option2,
      variant.option3
    ].filter(Boolean);

    for (const value of possibleValues) {
      const normalizedValue = this.normalize(value);

      if (availableValues.has(normalizedValue)) {
        return availableValues.get(normalizedValue);
      }
    }

    return null;
  }

  applyMedia(mediaId) {
    const variant = this.findVariantByMediaId(mediaId);

    if (!variant) {
      console.warn('[image-to-variant] No variant connected to media:', mediaId);
      return;
    }

    const colorValue = this.getColorValueFromVariant(variant);

    if (!colorValue) {
      console.warn('[image-to-variant] Could not find color value from variant:', variant);
      return;
    }

    this.selectColorValue(colorValue);
  }

  selectColorValue(colorValue) {
    this.updateColorBadge(colorValue);

    /**
     * Case 1:
     * Dropdown picker
     */
    const select = this.getTargetSelect();

    if (select) {
      if (select.value === colorValue) return;

      const optionExists = Array.from(select.options).some((option) => {
        return this.normalize(option.value) === this.normalize(colorValue);
      });

      if (!optionExists) {
        console.warn('[image-to-variant] No matching select option found for:', colorValue);
        return;
      }

      const matchedOption = Array.from(select.options).find((option) => {
        return this.normalize(option.value) === this.normalize(colorValue);
      });

      this.preventGalleryPrependDuringVariantChange(() => {
        select.value = matchedOption.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      });

      return;
    }

    /**
     * Case 2:
     * Swatch / button radio picker
     */
    const radios = this.getTargetRadios();

    const radio = radios.find((input) => {
      return this.normalize(input.value) === this.normalize(colorValue);
    });

    if (!radio) {
      console.warn('[image-to-variant] No matching radio found for:', colorValue);
      return;
    }

    if (radio.checked) return;

    this.preventGalleryPrependDuringVariantChange(() => {
      radio.click();
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  updateColorBadge(colorValue) {
    const badges = this.section.querySelectorAll(
      '[data-color-badge], [data-media-name-badge]'
    );

    badges.forEach((badge) => {
      badge.textContent = colorValue;
      badge.removeAttribute('hidden');
    });
  }

  preventGalleryPrependDuringVariantChange(callback) {
    const gallery = this.section.querySelector('media-gallery');
    let originalSetActiveMedia;

    if (gallery && typeof gallery.setActiveMedia === 'function') {
      originalSetActiveMedia = gallery.setActiveMedia;

      gallery.setActiveMedia = function (mediaId, _prepend) {
        return originalSetActiveMedia.call(this, mediaId, false);
      };
    }

    try {
      callback();
    } finally {
      if (gallery && originalSetActiveMedia) {
        gallery.setActiveMedia = originalSetActiveMedia;
      }
    }
  }
}

if (!customElements.get('image-to-variant')) {
  customElements.define('image-to-variant', ImageToVariant);
}